// =============================================================================
// RichTextEditorModal — a small modal WYSIWYG for the Action Sheet templates.
// =============================================================================
// Bold / Underline / Font-size (Small / Normal / Large) over a contentEditable
// surface. Saves the sanitised HTML subset (see src/services/richText.ts). Used
// by NextStepsAdmin. Placeholders like {{supplier}} are just literal text the
// admin types — a hint line lists them.
// =============================================================================
import React from "react";
import { FONT_SIZES, sanitizeRichText } from "../../services/richText";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function RichTextEditorModal({ title, initialHtml, hint, onSave, onCancel }) {
  const editorRef = React.useRef(null);

  // Seed once on open; the editor owns its DOM thereafter.
  React.useEffect(function () {
    if (editorRef.current) {
      editorRef.current.innerHTML = sanitizeRichText(initialHtml || "");
    }
  }, []);

  function exec(cmd) {
    try {
      document.execCommand("styleWithCSS", false, false);
      document.execCommand(cmd, false, null);
    } catch {
      // jsdom / unsupported — no-op
    }
    if (editorRef.current) editorRef.current.focus();
  }

  // Font size: wrap the selection in a size span (Small/Large) or strip sizes
  // (Normal). execCommand("fontSize") only supports legacy 1-7 sizes + <font>,
  // which our sanitiser drops — so do it directly.
  function applyFontSize(key) {
    const sel = window.getSelection ? window.getSelection() : null;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const frag = range.extractContents();
    // Clear any existing inline size within the selection first.
    frag.querySelectorAll?.("span[style]").forEach(function (s) {
      s.style.fontSize = "";
    });
    let node = frag;
    if (key !== "normal") {
      const span = document.createElement("span");
      span.style.fontSize = `${FONT_SIZES[key].px}px`;
      span.appendChild(frag);
      node = span;
    }
    range.insertNode(node);
    if (editorRef.current) editorRef.current.focus();
  }

  function handleSave() {
    const html = sanitizeRichText(editorRef.current ? editorRef.current.innerHTML : "");
    onSave(html);
  }

  const toolBtn = {
    background: "#fff",
    border: "1px solid #CFE0DE",
    borderRadius: 6,
    padding: "5px 10px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#1A2E2B",
  };

  return (
    <ModalBackdrop
      onClose={onCancel}
      contentStyle={{
        width: 660,
        maxWidth: "92vw",
        // ModalBackdrop's container is transparent by design — the caller
        // supplies the box. Without this the page bleeds through the title,
        // toolbar and Save/Cancel rows (only the editor surface was white).
        background: "#FFFFFF",
        borderRadius: 14,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      <div style={{ padding: 20 }}>
        <h2 className="m-0 mb-3 text-[18px] text-zephyr-text">{title || "Edit template"}</h2>

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <button
            type="button"
            style={{ ...toolBtn, fontWeight: 800 }}
            onClick={() => exec("bold")}
          >
            B
          </button>
          <button
            type="button"
            style={{ ...toolBtn, textDecoration: "underline" }}
            onClick={() => exec("underline")}
          >
            U
          </button>
          <span style={{ width: 1, background: "#CFE0DE", margin: "0 2px" }} />
          {["small", "normal", "large"].map(function (k) {
            return (
              <button
                type="button"
                key={k}
                style={toolBtn}
                onClick={() => applyFontSize(k)}
                title={`Font size: ${FONT_SIZES[k].label}`}
              >
                {FONT_SIZES[k].label}
              </button>
            );
          })}
        </div>

        {/* Editable surface */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          data-testid="rich-editor"
          className="w-full rounded-[8px] border border-zephyr-border text-[14px] text-zephyr-text bg-white box-border"
          style={{
            minHeight: 180,
            padding: "10px 12px",
            lineHeight: 1.6,
            overflowY: "auto",
            outline: "none",
            fontFamily: "inherit",
            whiteSpace: "pre-wrap",
          }}
        />

        {hint && <div className="mt-2 text-[12px] text-zephyr-muted">{hint}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
          <Btn onClick={handleSave}>Save</Btn>
          <button
            type="button"
            onClick={onCancel}
            className="bg-transparent border border-zephyr-border rounded-[6px] py-[6px] px-[14px] text-xs cursor-pointer text-zephyr-muted"
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
